// Per spec/blocks.md §time-location-cost (P-037.28 / D-136). Atomic
// 3-col info block — fixed headers ("Time" / "Location" / "Cost") +
// staff-editable richText bodies per column. Designed for event-meta
// composition under big-3-hero-image / event-card-group / big3-
// signature-events.
//
// **Render:**
//   ┌─────────────────────────────────────────────────────────┐
//   │ Time             Location         Cost                  │
//   │ {time-body}      {location-body}  {cost-body}           │
//   └─────────────────────────────────────────────────────────┘
//
// **Outer cell bg = `ctx.tintColor`** (per-Header 10% pastel from
// D-131 §1). Reuses the same context field as the footer's bg + the
// big3-signature-events outer table; staff sees an event-meta panel
// that visually inherits the per-Email family.
//
// **Layout:** 30 px L/R outer padding; 3-col grid via the system
// `gridLayout` 3-col helper (30/190/25/190/25/190/30 per D-117).
// Each column renders the fixed header (16 px bold accent-navy-deep —
// matches the EventCard / Big3EventCard title typography per D-130 /
// D-132) + a Tiptap richText body (14 px text-primary, sanitized via
// DOMPurify — list extensions per D-136 flow through automatically).
//
// **All 3 fields required** — standard Zod min(1) validation surfaces
// inline errors if any are empty. No autoHide condition needed
// (Block is not array-based; staff doesn't end up with empty fields
// post-Zod-parse).
//
// **P-037.29 post-Phase-Close §item F (Banks's text-Block top-padding
// fix).** Per-column bodies now flow through `richTextBody()` instead
// of the `text()` primitive. The previous wrapper produced `<p>` nested
// inside `<p>` — HTML5 closes the outer when the inner opens, so the
// styling was wasted and bodies inherited UA-default font with a 1em
// top margin (~14–16 px of phantom space above each column body). The
// column-header still uses the `text()` primitive because that text is
// a plain string, not a sanitized richText HtmlString — no nesting
// problem. See `src/render/rich-text-body.ts` for the body-rendering
// rationale.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { richTextBody } from '../render/rich-text-body'
import { sanitizeRichText } from '../render/sanitize'
import { tokens } from '../tokens'
import { text } from '../primitives/text'
import { gridLayout } from '../components/helpers'
import { HtmlString } from '../types'

const optionsSchema = z.object({})

const inputsSchema = z.object({
  time: z.string().min(1).describe('richText'),
  location: z.string().min(1).describe('richText'),
  cost: z.string().min(1).describe('richText'),
})

interface Column {
  header: string
  body: string
}

const HEADER_SIZE = 16
const BODY_SIZE = tokens.type.size.sm
const HEADER_BODY_GAP = tokens.space[3]
const NAVY = tokens.color['accent-navy-deep']

function columnCard(col: Column): HtmlString {
  const headerHtml = text({
    text: col.header,
    size: HEADER_SIZE,
    weight: tokens.type.weight.bold,
    color: NAVY,
  })
  const bodyHtml = richTextBody(sanitizeRichText(col.body), { size: BODY_SIZE })
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td>${headerHtml}</td></tr><tr><td style="padding-top: ${HEADER_BODY_GAP}px;">${bodyHtml}</td></tr></table>`
}

export const timeLocationCost = defineBlock({
  id: 'time-location-cost',
  label: 'Time / Location / Cost',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: {
    time: '<p>7:00 PM</p>',
    location: '<p>The Four Seasons</p>',
    cost: '<p>$95 per person</p>',
  },
  render: ({ inputs }, ctx) => {
    const columns: Column[] = [
      { header: 'Time', body: inputs.time },
      { header: 'Location', body: inputs.location },
      { header: 'Cost', body: inputs.cost },
    ]
    const grid = gridLayout(columns, 3, columnCard)
    // Outer table bg = ctx.tintColor (D-131). 30 px L/R + 30 px top
    // + 30 px bottom — matches big3-signature-events's per-Header
    // tinted-panel rhythm. Width=100% lets the bg span the full email
    // body width inside the document shell.
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" bgcolor="${ctx.tintColor}" style="background-color: ${ctx.tintColor};"><tr><td height="30" style="height: 30px; line-height: 30px; font-size: 30px; mso-line-height-rule: exactly;">&nbsp;</td></tr><tr><td style="padding: 0 30px;">${grid}</td></tr><tr><td height="30" style="height: 30px; line-height: 30px; font-size: 30px; mso-line-height-rule: exactly;">&nbsp;</td></tr></table>`
  },
})
